import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Check, PlusCircle } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { getImageUrl } from '@/utils/imageUrl';
import i18n from '@/utils/i18n';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Checkbox } from '@/components/ui/checkbox';

export type MinimalContainerType = {
  id: number;
  name: string;
  imageUrl: string | null | undefined;
  hexCode?: string | null;
  disabled?: boolean;
  disabledTooltip?: string | null;
};

interface ContainerTypeDropdownContentProps {
  containerTypes: MinimalContainerType[];
  onSelect: (ct: MinimalContainerType) => void;
  selectedIds?: Set<number>;
  searchPlaceholder?: string;
  emptyText?: string;
  children?: React.ReactNode;
}

export function ContainerTypeDropdownContent({
  containerTypes,
  onSelect,
  selectedIds,
  searchPlaceholder = i18n.t('Order:filterSearchContainerType'),
  emptyText = i18n.t('Order:filterNoContainerTypes'),
  children,
}: ContainerTypeDropdownContentProps) {
  const parentRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    if (!search) return containerTypes;
    const lowerSearch = search.toLowerCase();
    return containerTypes.filter((ct) =>
      ct.name.toLowerCase().includes(lowerSearch),
    );
  }, [containerTypes, search]);

  const rowVirtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 50,
    overscan: 5,
  });

  return (
    <Command shouldFilter={false}>
      <CommandInput
        placeholder={searchPlaceholder}
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        {filtered.length === 0 && <CommandEmpty>{emptyText}</CommandEmpty>}
        <CommandGroup ref={parentRef}>
          {rowVirtualizer.getVirtualItems().map((virtualRow) => {
            const ct = filtered[virtualRow.index];
            const isSelected = selectedIds?.has(ct.id) ?? false;
            const isDisabled = ct.disabled === true;
            return (
              <CommandItem
                key={ct.id}
                value={`${ct.id}-${ct.name}`}
                onSelect={() => {
                  if (isDisabled) return;
                  onSelect(ct);
                }}
                disabled={isDisabled}
                className="tw:group/command-item"
              >
                {selectedIds && (
                  <div
                    className={cn(
                      'tw:flex tw:size-4 tw:items-center tw:justify-center tw:rounded-[4px] tw:border',
                      isSelected
                        ? 'tw:bg-primary tw:border-primary'
                        : 'tw:border-input tw:[&_svg]:invisible',
                    )}
                  >
                    <Check
                      className={cn(
                        'tw:size-3.5',
                        isSelected && 'tw:text-white! tw:dark:text-black!',
                      )}
                    />
                  </div>
                )}
                <Avatar className="tw:size-10">
                  <AvatarImage
                    src={getImageUrl(64, ct.imageUrl)}
                    className="tw:object-contain"
                    alt={ct.name}
                  />
                  <AvatarFallback className="tw:group-data-[selected=true]/command-item:bg-white">
                    {ct.name.charAt(0)}
                  </AvatarFallback>
                </Avatar>
                <div className="tw:flex tw:min-w-0 tw:flex-col">
                  <span
                    className="tw:line-clamp-2 tw:break-words"
                    title={ct.name}
                  >
                    {ct.name}
                  </span>
                  {isDisabled && ct.disabledTooltip && (
                    <span
                      className="tw:text-muted-foreground tw:line-clamp-2 tw:break-words tw:text-xs"
                      title={ct.disabledTooltip}
                    >
                      {ct.disabledTooltip}
                    </span>
                  )}
                </div>
              </CommandItem>
            );
          })}
        </CommandGroup>
      </CommandList>
      {children}
    </Command>
  );
}

/**
 * What a group's entries are. Selection handlers switch on this, never on
 * the (translated) heading.
 */
export type ItemTypeGroupKind =
  | 'container'
  | 'container_station'
  | 'asset'
  | 'untracked';

export interface ItemTypeGroup {
  kind: ItemTypeGroupKind;
  heading: string;
  items: MinimalContainerType[];
}

interface GroupedItemTypeDropdownContentProps {
  groups: ItemTypeGroup[];
  onSelect: (ct: MinimalContainerType, group: ItemTypeGroup) => void;
  searchPlaceholder?: string;
  emptyText?: string;
}

export function GroupedItemTypeDropdownContent({
  groups,
  onSelect,
  searchPlaceholder = i18n.t('Order:filterSearchContainerType'),
  emptyText = i18n.t('Order:filterNoContainerTypes'),
}: GroupedItemTypeDropdownContentProps) {
  const [search, setSearch] = useState('');

  const filteredGroups = (() => {
    const lower = search.toLowerCase();
    return groups
      .map((group) => ({
        ...group,
        items: search
          ? group.items.filter((ct) => ct.name.toLowerCase().includes(lower))
          : group.items,
      }))
      .filter((group) => group.items.length > 0);
  })();

  return (
    <Command shouldFilter={false}>
      <CommandInput
        placeholder={searchPlaceholder}
        value={search}
        onValueChange={setSearch}
      />
      <CommandList>
        {filteredGroups.length === 0 && (
          <CommandEmpty>{emptyText}</CommandEmpty>
        )}
        {filteredGroups.map((group, gi) => (
          <React.Fragment key={group.kind}>
            {gi > 0 && <CommandSeparator />}
            <CommandGroup heading={group.heading}>
              {group.items.map((ct) => (
                <CommandItem
                  key={ct.id}
                  value={`${ct.id}-${ct.name}`}
                  onSelect={() => onSelect(ct, group)}
                >
                  <Avatar className="tw:size-10">
                    <AvatarImage
                      src={getImageUrl(64, ct.imageUrl)}
                      className="tw:object-contain"
                      alt={ct.name}
                    />
                    <AvatarFallback>{ct.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <div className="tw:flex tw:min-w-0 tw:flex-col">
                    <span
                      className="tw:line-clamp-2 tw:break-words"
                      title={ct.name}
                    >
                      {ct.name}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </React.Fragment>
        ))}
      </CommandList>
    </Command>
  );
}

export interface ContainerTypeComboboxProps {
  containerTypes: MinimalContainerType[];
  selected: MinimalContainerType[];
  onSelect: (containerTypes: MinimalContainerType[]) => void;
  multiple?: boolean;
  popoverModal?: boolean;
}

export function ContainerTypeDropdown({
  containerTypes,
  selected,
  onSelect,
  multiple = true,
  popoverModal,
}: ContainerTypeComboboxProps) {
  const selectedIds = useMemo(
    () => new Set(selected.map((ct) => ct.id)),
    [selected],
  );

  const triggerBadge =
    selected.length === 0
      ? null
      : selected.length === 1
        ? selected[0].name
        : i18n.t('Common:countSelected', { count: selected.length });

  const handleSelect = useCallback(
    (ct: MinimalContainerType) => {
      if (multiple) {
        const exists = selectedIds.has(ct.id);
        if (exists) {
          onSelect(selected.filter((s) => s.id !== ct.id));
        } else {
          onSelect([...selected, ct]);
        }
      } else {
        onSelect(selectedIds.has(ct.id) ? [] : [ct]);
      }
    },
    [multiple, onSelect, selected, selectedIds],
  );

  return (
    <Popover modal={popoverModal}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="tw:border-dashed">
          {selected.length === 0 && <PlusCircle />}
          {i18n.t('Order:filterContainerType')}
          {triggerBadge && (
            <>
              <Separator orientation="vertical" className="tw:mx-2 tw:h-full" />
              <Badge
                variant="secondary"
                className="tw:rounded-sm tw:px-1 tw:font-normal tw:max-w-[150px]"
              >
                <span className="tw:truncate">{triggerBadge}</span>
              </Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="tw:w-[380px] tw:p-0" align="start">
        <ContainerTypeDropdownContent
          containerTypes={containerTypes}
          onSelect={handleSelect}
          selectedIds={selectedIds}
        />
      </PopoverContent>
    </Popover>
  );
}
